import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';

dayjs.extend(relativeTime);
dayjs.extend(utc);

export const LOCALE = 'en-GB';

/** "3 min ago", "in 2 days", "just now". `now` is injectable for tests. */
export function relTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const diffS = Math.round((t - now.getTime()) / 1000);
  const abs = Math.abs(diffS);
  if (abs < 45) return 'just now';
  let value = abs;
  let unit = 'sec';
  if (abs < 3600) {
    value = Math.round(abs / 60);
    unit = 'min';
  } else if (abs < 86400) {
    value = Math.round(abs / 3600);
    unit = 'h';
  } else if (abs < 86400 * 30) {
    value = Math.round(abs / 86400);
    unit = value === 1 ? 'day' : 'days';
  } else if (abs < 86400 * 365) {
    value = Math.round(abs / (86400 * 30));
    unit = value === 1 ? 'month' : 'months';
  } else {
    value = Math.round(abs / (86400 * 365));
    unit = value === 1 ? 'year' : 'years';
  }
  const s = `${value} ${unit}`;
  return diffS < 0 ? `${s} ago` : `in ${s}`;
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = dayjs(iso);
  return d.isValid() ? d.format('D MMM YYYY, HH:mm') : '—';
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = dayjs(iso);
  return d.isValid() ? d.format('D MMM YYYY') : '—';
}

export function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = dayjs(iso);
  return d.isValid() ? d.format('HH:mm:ss') : '';
}

export function fmtNumber(n: number | null | undefined, digits = 0): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return new Intl.NumberFormat(LOCALE, { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(n);
}

/** Compact: 1 234 → "1.2K". */
export function fmtCompact(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return new Intl.NumberFormat(LOCALE, { notation: 'compact', maximumFractionDigits: 1 }).format(n);
}

export function fmtMoney(n: number | null | undefined, currency: string | null | undefined, opts: { compact?: boolean } = {}): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  if (!currency) return fmtNumber(n, 2);
  try {
    return new Intl.NumberFormat(LOCALE, {
      style: 'currency',
      currency,
      notation: opts.compact ? 'compact' : 'standard',
      maximumFractionDigits: opts.compact ? 1 : n % 1 === 0 ? 0 : 2,
    }).format(n);
  } catch {
    return `${fmtNumber(n, 2)} ${currency}`;
  }
}

export function fmtPct(n: number | null | undefined, digits = 0): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return `${fmtNumber(n, digits)}%`;
}

export function fmtBytes(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${fmtNumber(v, v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

/** Hours → "5 h", "2 days". */
export function fmtAgeHours(h: number | null | undefined): string {
  if (h === null || h === undefined) return '—';
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} min`;
  if (h < 48) return `${Math.round(h)} h`;
  return `${Math.round(h / 24)} days`;
}

export function daysUntil(iso: string | null | undefined, now: Date = new Date()): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.ceil((t - now.getTime()) / 86400000);
}
